import { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Field } from '../ui/Field';
import { Button } from '../ui/Button';
import { BrandAvatar, brandInitials } from '../../lib/brandAvatars';
import { Brand, BrandPriority } from '../../lib/types';
import { slugify, useCreateBrand, useUpdateBrand } from '../../lib/api/brands';
import { useToast } from '../ui/Toast';
import BrandLogoCropper from './BrandLogoCropper';

const SWATCHES = [
  '#FFFFFF', '#1C1917', '#78716C', '#E2542C',
  '#D97706', '#7C2D12', '#15803D', '#0F766E',
  '#1E40AF', '#0EA5E9', '#BE185D', '#7C3AED',
];

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated?: (id: string) => void;
  /** If set, modal switches to edit mode: patches this brand instead of creating. */
  brand?: Brand | null;
}

export const CreateBrandModal = ({ open, onClose, onCreated, brand }: Props) => {
  const toast = useToast();
  const createBrand = useCreateBrand();
  const updateBrand = useUpdateBrand();
  const editing = !!brand;

  const [name, setName] = useState('');
  const [country, setCountry] = useState('');
  const [priority, setPriority] = useState<BrandPriority>('B');
  const [color, setColor] = useState(SWATCHES[0]);
  const [logo, setLogo] = useState('');
  const [permanentDiscount, setPermanentDiscount] = useState('');
  const [pimVendor, setPimVendor] = useState('');
  const [avgMargin, setAvgMargin] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [mediaBankUrl, setMediaBankUrl] = useState('');
  const [mediaBankAccess, setMediaBankAccess] = useState('');
  const [cropSource, setCropSource] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (brand) {
      setName(brand.name);
      setCountry(brand.country ?? '');
      setPriority((brand.priority as BrandPriority) ?? 'B');
      setColor(brand.color);
      setLogo(brand.logo ?? '');
      setPermanentDiscount(brand.permanent_discount ?? '');
      setPimVendor(brand.pim_vendor ?? '');
      setAvgMargin(brand.margin ?? '');
      setWebsiteUrl(brand.website_url ?? '');
      setMediaBankUrl(brand.media_bank_url ?? '');
      setMediaBankAccess(brand.media_bank_access ?? '');
    } else {
      setName('');
      setCountry('');
      setPriority('B');
      setColor(SWATCHES[0]);
      setLogo('');
      setPermanentDiscount('');
      setPimVendor('');
      setAvgMargin('');
      setWebsiteUrl('');
      setMediaBankUrl('');
      setMediaBankAccess('');
    }
    setCropSource(null);
  }, [open, brand]);

  const id = editing ? brand!.id : slugify(name);
  const pending = createBrand.isPending || updateBrand.isPending;
  const canSubmit = !!name.trim() && !!id && !pending;

  const onFile = (file: File | null) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      // Open the cropper instead of saving the raw upload directly
      if (dataUrl) setCropSource(dataUrl);
    };
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!canSubmit) return;
    try {
      if (editing) {
        const updated = await updateBrand.mutateAsync({
          id: brand!.id,
          patch: {
            name: name.trim(),
            ...(brand!.country_source === 'dory' ? {} : { country: country.trim().toUpperCase() }),
            priority,
            color,
            logo,
            permanent_discount: permanentDiscount.trim(),
            pim_vendor: pimVendor.trim(),
            website_url: websiteUrl.trim(),
            media_bank_url: mediaBankUrl.trim(),
            media_bank_access: mediaBankAccess.trim(),
          },
        });
        toast.push(`Brand "${updated.name}" updated`);
        onCreated?.(updated.id);
      } else {
        const created = await createBrand.mutateAsync({
          id,
          name: name.trim(),
          country: country.trim().toUpperCase(),
          priority,
          color,
          logo,
          permanent_discount: permanentDiscount.trim(),
          pim_vendor: pimVendor.trim(),
          website_url: websiteUrl.trim(),
          media_bank_url: mediaBankUrl.trim(),
          media_bank_access: mediaBankAccess.trim(),
        });
        toast.push(`Brand "${created.name}" created`);
        onCreated?.(created.id);
      }
      onClose();
    } catch (err) {
      console.error(err);
      toast.push(editing ? 'Could not update brand' : 'Could not create brand');
    }
  };

  const previewBrand = { name: name || 'Brand', color, logo: logo || null };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? `Edit brand — ${brand!.name}` : 'New brand'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={!canSubmit}>
            {editing ? 'Save changes' : 'Create brand'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
        <BrandAvatar brand={previewBrand} size={52} />
        <div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>{name || 'Preview'}</div>
          <div style={{ fontSize: 11.5, color: 'var(--ink-4)' }}>
            {country ? country.toUpperCase() : '—'}
          </div>
        </div>
      </div>

      <Field label="Brand name" required>
        <input
          className="input"
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Flos"
        />
      </Field>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field
          label="Country"
          hint={brand?.country_source === 'dory'
            ? `Comes from Dory (Design country: ${brand.country_name || brand.country}). Change it on the brand's identity card in Dory.`
            : undefined}
        >
          <input
            className="input"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            placeholder="IT"
            maxLength={4}
            readOnly={brand?.country_source === 'dory'}
            disabled={brand?.country_source === 'dory'}
          />
        </Field>
        {/* Priority (A/B/C) is kept on the brand but not shown or edited
            here for now (Doreen, 2026-10-12); the stored value is sent back
            unchanged so nothing is lost. */}
      </div>

      <Field
        label="PIM vendor name"
        hint='Only needed when the spelling differs from the brand name here — e.g. "DCWéditions". Used when setting up pricing campaigns in Pricy.'
      >
        <input
          className="input"
          value={pimVendor}
          onChange={(e) => setPimVendor(e.target.value)}
          placeholder={name || 'Same as brand name'}
        />
      </Field>

      <Field label="Website" hint="The brand's own site. Shared with Dory.">
        <input
          className="input"
          value={websiteUrl}
          onChange={(e) => setWebsiteUrl(e.target.value)}
          placeholder="https://"
        />
      </Field>

      <Field
        label="Media bank"
        hint="Presscloud, image bank or download database where we fetch photos and files. Shared with Dory."
      >
        <input
          className="input"
          value={mediaBankUrl}
          onChange={(e) => setMediaBankUrl(e.target.value)}
          placeholder="https://brand.presscloud.com"
        />
      </Field>

      <Field label="How to get in" hint="Login details, who to ask for access, quirks.">
        <textarea
          className="input"
          value={mediaBankAccess}
          onChange={(e) => setMediaBankAccess(e.target.value)}
          placeholder="e.g. Login with the marketing@ mailbox · ask Mette for new accounts"
          rows={2}
          style={{ resize: 'vertical' }}
        />
      </Field>

      <Field
        label="Permanent discount"
        hint="Set from Pricy campaigns + the live catalog average, refreshed weekly — read-only here. Auto-prefills the discount column on partnership tracking rows."
      >
        <input
          className="input"
          value={permanentDiscount}
          readOnly
          disabled
          title="Pricy / script-driven — updated by the weekly sync, not editable"
          placeholder="—"
        />
      </Field>

      <Field
        label="Margin"
        hint="Agreed supplier discount from Dory's cost rule when the brand has one; otherwise the average margin measured weekly across the catalog. Read-only."
      >
        <input
          className="input"
          value={avgMargin}
          readOnly
          disabled
          title="Script-driven — updated by the weekly sync, not editable"
          placeholder="—"
        />
      </Field>

      <Field label="Brand color" hint="Pick a swatch or set a custom colour.">
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {SWATCHES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setColor(s)}
              style={{
                width: 26,
                height: 26,
                borderRadius: 8,
                background: s,
                border:
                  color === s
                    ? '2px solid var(--ink)'
                    : `1px solid ${s.toUpperCase() === '#FFFFFF' ? 'var(--border-strong)' : 'var(--border)'}`,
                cursor: 'pointer',
              }}
              aria-label={s}
              title={s}
            />
          ))}
          <span
            style={{
              width: 1,
              height: 22,
              background: 'var(--border)',
              margin: '0 4px',
            }}
          />
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              cursor: 'pointer',
              border: SWATCHES.includes(color)
                ? '1px dashed var(--border-strong)'
                : '2px solid var(--ink)',
              borderRadius: 8,
              padding: '2px 8px 2px 4px',
              fontSize: 11.5,
              color: 'var(--ink-3)',
              height: 26,
              boxSizing: 'border-box',
            }}
            title="Pick a custom colour"
          >
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value.toUpperCase())}
              style={{
                width: 22,
                height: 22,
                border: 'none',
                padding: 0,
                background: 'transparent',
                cursor: 'pointer',
              }}
            />
            Custom
          </label>
        </div>
      </Field>

      <Field label="Logo (optional)" hint={logo ? 'Uploaded' : 'Upload a PNG/SVG with transparent background'}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => onFile(e.target.files?.[0] ?? null)}
            style={{ fontSize: 12 }}
          />
          {logo && (
            <Button variant="ghost" onClick={() => setLogo('')}>
              Remove
            </Button>
          )}
        </div>
      </Field>

      {!editing && name && (
        <div style={{ marginTop: 10, fontSize: 11, color: 'var(--ink-4)' }}>
          Slug: <span className="mono">{id}</span>
          {brandInitials(name) && (
            <span style={{ marginLeft: 12 }}>
              Initials: <span className="mono">{brandInitials(name)}</span>
            </span>
          )}
        </div>
      )}
      {editing && (
        <div style={{ marginTop: 10, fontSize: 11, color: 'var(--ink-4)' }}>
          Slug: <span className="mono">{brand!.id}</span> (cannot be changed)
        </div>
      )}
      <BrandLogoCropper
        open={!!cropSource}
        imageSrc={cropSource ?? ''}
        onClose={() => setCropSource(null)}
        onSave={(cropped) => {
          setLogo(cropped);
          setCropSource(null);
        }}
      />
    </Modal>
  );
};

export default CreateBrandModal;
